import { useEffect, useMemo, useRef, useState } from 'react';

import { bindAll } from 'bind-event-listener';

import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import type {
	ApiTaskStatus,
	ConversationChannel,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import { AssistanceServiceImpl } from '@atlassian/conversation-assistant-service/services/assistance-service/main';

import { useAISmartButtonContext } from '../../context';
import { isBrowser } from '../../utils/is-browser';
import { schedulePostPaint } from '../../utils/schedule-post-paint';

/**
 * Task states where the agent is blocked waiting on user input (plan,
 * disambiguation, or action confirmation). Mirrors the mode-agnostic
 * `isTaskStatusAwaitingUserResponse` helper in
 * `@atlassian/conversation-assistant-ui-components` — inlined here to avoid
 * pulling the UI-components package into the smart-button dependency graph for
 * a single pure predicate. Status alone is sufficient: the AWAITING_* codes are
 * only produced by max-mode conversations, so no mode check is required.
 * (TREX-1890 / TREX-1892)
 */
function isTaskStatusAwaitingUserResponse(taskStatus: ApiTaskStatus | undefined): boolean {
	return (
		taskStatus === 'AWAITING_DISAMBIGUATION' ||
		taskStatus === 'AWAITING_PLAN_CONFIRMATION' ||
		taskStatus === 'AWAITING_ACTION_CONFIRMATION'
	);
}

/** A conversation is "awaiting input" when any of its tasks is blocked on the user. */
function isChannelAwaitingInput(channel: ConversationChannel): boolean {
	return !!channel.tasks?.some((task) => isTaskStatusAwaitingUserResponse(task.status));
}

const LOOK_BACK_PERIOD_MS = 36 * 60 * 60 * 1000;
const CACHE_TTL_MS = 5 * 60 * 1000;
const IN_PROGRESS_POLL_BASE_INTERVAL_MS = 60 * 1000;
const IN_PROGRESS_POLL_BACKOFF_STEP_MS = 30 * 1000;
const IN_PROGRESS_POLL_ATTEMPTS_PER_STEP = 5;
const IN_PROGRESS_POLL_MAX_INTERVAL_MS = CACHE_TTL_MS;
const CACHE_KEY = 'rovo-button-unread-status-v1';

// The cache key is shared across hook instances, so network writes need a shared ordering guard.
let nextFetchSequence = 0;
let latestWrittenFetchSequence = 0;

type ConversationStatus = { completedAt: string | null; id: string; name: string | null };

type AwaitingInputStatus = { id: string; name: string | null };

type UnreadConversationSummary = {
	awaitingInputCount: number;
	hasUnread: boolean;
	inProgressCount: number;
	isStatusReady: boolean;
	mostRecentAwaitingInput: AwaitingInputStatus | null;
	mostRecentInProgress: ConversationStatus | null;
	mostRecentUnread: ConversationStatus | null;
	unreadCount: number;
};

type CachedResult = Omit<UnreadConversationSummary, 'isStatusReady'> & {
	cachedAt: number;
	stale?: boolean;
};

function normalizeCachedResult(result: Partial<CachedResult>): UnreadConversationSummary {
	const unreadCount = result.unreadCount ?? (result.hasUnread ? 1 : 0);
	return {
		hasUnread: unreadCount > 0,
		unreadCount,
		inProgressCount: result.inProgressCount ?? 0,
		awaitingInputCount: result.awaitingInputCount ?? 0,
		mostRecentAwaitingInput: result.mostRecentAwaitingInput ?? null,
		mostRecentInProgress: result.mostRecentInProgress ?? null,
		mostRecentUnread: result.mostRecentUnread ?? null,
		isStatusReady: true,
	};
}

function areConversationStatusesEqual(
	a: ConversationStatus | AwaitingInputStatus | null,
	b: ConversationStatus | AwaitingInputStatus | null,
): boolean {
	if (a === b) {
		return true;
	}
	if (!a || !b) {
		return false;
	}
	return (
		a.id === b.id &&
		a.name === b.name &&
		('completedAt' in a ? a.completedAt : null) === ('completedAt' in b ? b.completedAt : null)
	);
}

function areSummariesEqual(a: UnreadConversationSummary, b: UnreadConversationSummary): boolean {
	return (
		a.hasUnread === b.hasUnread &&
		a.unreadCount === b.unreadCount &&
		a.inProgressCount === b.inProgressCount &&
		a.awaitingInputCount === b.awaitingInputCount &&
		areConversationStatusesEqual(a.mostRecentUnread, b.mostRecentUnread) &&
		areConversationStatusesEqual(a.mostRecentInProgress, b.mostRecentInProgress) &&
		areConversationStatusesEqual(a.mostRecentAwaitingInput, b.mostRecentAwaitingInput) &&
		a.isStatusReady === b.isStatusReady
	);
}

function readCache(): { data: UnreadConversationSummary; isStale: boolean } | null {
	if (!isBrowser()) {
		return null;
	}
	try {
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
		const raw = sessionStorage.getItem(CACHE_KEY);
		if (!raw) {
			return null;
		}
		const parsed: Partial<CachedResult> = JSON.parse(raw);
		const { cachedAt = 0, stale, ...result } = parsed;
		const isExpired = !cachedAt || Date.now() - cachedAt >= CACHE_TTL_MS;
		const data = normalizeCachedResult(result);
		return { data, isStale: stale === true || isExpired };
	} catch {
		return null;
	}
}

function writeCache(result: UnreadConversationSummary, cachedAt = Date.now()): void {
	const { isStatusReady: _isStatusReady, ...cacheableResult } = result;
	const toStore: CachedResult = { ...cacheableResult, cachedAt };
	if (!isBrowser()) {
		return;
	}
	try {
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
		sessionStorage.setItem(CACHE_KEY, JSON.stringify(toStore));
	} catch {
		// sessionStorage may be unavailable (private browsing quota, etc.) — fail silently
	}
}

/**
 * Marks the cached unread conversation status as stale without removing it.
 * The cached value continues to be displayed while a background re-fetch
 * updates the indicator — stale-while-revalidate.
 *
 * Call this directly when the user closes the mini-modal or sidebar. After marking
 * stale, the next call to `fetchAndUpdate` (triggered by post-paint scheduling or
 * window focus) will show the cached value immediately while re-fetching in the
 * background, then update the indicator once the fresh result arrives.
 */
export function markUnreadCacheStale(): void {
	if (!isBrowser()) {
		return;
	}
	try {
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
		const raw = sessionStorage.getItem(CACHE_KEY);
		if (!raw) {
			return;
		}
		const parsed: Partial<CachedResult> = JSON.parse(raw);
		const { isStatusReady: _isStatusReady, ...cacheableResult } = normalizeCachedResult(parsed);
		const cachedAt = parsed.cachedAt ?? Date.now();
		const toStore: CachedResult = { ...cacheableResult, cachedAt, stale: true };
		// eslint-disable-next-line @atlaskit/platform/no-direct-web-storage-usage -- existing usage
		sessionStorage.setItem(CACHE_KEY, JSON.stringify(toStore));
	} catch {
		// sessionStorage may be unavailable — fail silently
	}
}

const NO_UNREAD: UnreadConversationSummary = {
	hasUnread: false,
	unreadCount: 0,
	inProgressCount: 0,
	awaitingInputCount: 0,
	mostRecentAwaitingInput: null,
	mostRecentInProgress: null,
	mostRecentUnread: null,
	isStatusReady: false,
};

/**
 * Fetches conversation channel data after the page has painted (post-vc90) and
 * derives whether the user has any unread conversations from the past 36 hours.
 *
 * Results are cached in sessionStorage with a 5-minute TTL and a stale-while-revalidate
 * strategy: when the cache is marked stale (e.g. after the user closes the mini-modal or
 * sidebar), the cached value is shown immediately while a background re-fetch updates the
 * indicator without causing a flash.
 *
 * On any fetch failure, the currently displayed status is preserved rather than resetting
 * to no-indicator — a false negative is worse UX than a briefly stale indicator.
 */
export function useUnreadConversationStatus({
	enabled,
	mode,
}: {
	enabled: boolean;
	mode?: string;
}): UnreadConversationSummary {
	const initialCacheRef = useRef(enabled ? readCache() : null);
	const [status, setStatus] = useState<UnreadConversationSummary>(() =>
		enabled ? (initialCacheRef.current?.data ?? NO_UNREAD) : NO_UNREAD,
	);
	const { conversationAssistantConfig, product } = useAISmartButtonContext();
	const baseUrl = conversationAssistantConfig?.assistanceServiceConfig?.baseUrl;
	const headers = conversationAssistantConfig?.assistanceServiceConfig?.headers;
	const headersKey = headers ? JSON.stringify(headers) : '';
	const assistanceService = useMemo(
		() =>
			new AssistanceServiceImpl({
				product,
				experienceId: 'ai-mate',
				baseUrl,
				headers,
			}),
		// headersKey is the stable proxy for the headers object (which gets a new
		// identity every render from context); keying on it prevents assistanceService
		// from being recreated on every render, which otherwise re-runs the fetch
		// effect on each hover-induced re-render.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[product, baseUrl, headersKey],
	);

	const fetchAndUpdateRef = useRef<(options?: { forceNetwork?: boolean }) => Promise<void>>(
		async () => {},
	);
	const isFetchingRef = useRef(false);
	const hasMarkedInitialCacheStaleRef = useRef(false);
	const prevModeRef = useRef<string | undefined>(mode);

	useEffect(() => {
		if (!enabled) {
			// Reset the in-flight guard so the next enable can fetch immediately.
			// Without this, a fetch that was in-flight when enabled flipped to false
			// would leave isFetchingRef stuck as true, blocking the next fetch.
			isFetchingRef.current = false;
			hasMarkedInitialCacheStaleRef.current = false;
			setStatus(NO_UNREAD);
			return;
		}

		let cancelled = false;

		const fetchAndUpdate = async (options?: { forceNetwork?: boolean }) => {
			const forceNetwork = options?.forceNetwork ?? false;
			const cacheResult = forceNetwork ? null : readCache();
			if (cacheResult) {
				if (!cancelled) {
					setStatus((prev) => {
						return areSummariesEqual(prev, cacheResult.data) ? prev : cacheResult.data;
					});
				}
				if (!cacheResult.isStale) {
					return;
				}
				// Stale cache is still the last good read value: keep it visible
				// while a background fetch refreshes every derived field.
			}

			if (isFetchingRef.current) {
				return;
			}
			isFetchingRef.current = true;
			try {
				const fetchSequence = ++nextFetchSequence;
				const { items: channels } = await assistanceService.getConversationChannels();
				if (fetchSequence < latestWrittenFetchSequence) {
					return;
				}

				const now = Date.now();

				// Conversations blocked waiting on user input (max-mode plan /
				// disambiguation / action confirmation), most-recent first so the UI can
				// deep-link to the single awaiting chat. (TREX-1890 / TREX-1892)
				//
				// Apply the SAME recency guard as the unread filter below: task arrays in
				// the channels payload are cumulative and can retain a historical
				// AWAITING_* entry after the user has already responded, so without a
				// look-back window an old/resolved conversation would be counted forever
				// (inflating the count, making the pill wrongly interactive, and
				// deep-linking to a stale conversation id). (TREX-1917)
				const awaitingInput = channels
					.filter((channel) => {
						if (!isChannelAwaitingInput(channel)) {
							return false;
						}
						const timestamp = channel.last_message_timestamp;
						return !!timestamp && now - new Date(timestamp).getTime() < LOOK_BACK_PERIOD_MS;
					})
					.sort((a, b) => {
						const aTime = a.last_message_timestamp
							? new Date(a.last_message_timestamp).getTime()
							: 0;
						const bTime = b.last_message_timestamp
							? new Date(b.last_message_timestamp).getTime()
							: 0;
						return bTime - aTime;
					});
				const awaitingInputCount = awaitingInput.length;
				const mostRecentAwaitingInput = awaitingInput[0]
					? { id: String(awaitingInput[0].id), name: awaitingInput[0].name ?? null }
					: null;
				const awaitingInputIds = new Set(awaitingInput.map((channel) => String(channel.id)));

				const recentInProgress = channels
					.filter(
						(channel) =>
							channel.last_agent_message_status === 'IN_PROGRESS' &&
							!awaitingInputIds.has(String(channel.id)),
					)
					.sort((a, b) => {
						const aTime = a.last_message_timestamp
							? new Date(a.last_message_timestamp).getTime()
							: 0;
						const bTime = b.last_message_timestamp
							? new Date(b.last_message_timestamp).getTime()
							: 0;
						return bTime - aTime;
					});
				const inProgressCount = recentInProgress.length;
				const mostRecentInProgress = recentInProgress[0]
					? {
							id: String(recentInProgress[0].id),
							name: recentInProgress[0].name ?? null,
							completedAt: recentInProgress[0].last_message_timestamp ?? null,
						}
					: null;

				const recentUnread = channels
					.filter((channel) => {
						// readStatus === false is the explicit unread signal; null means a legacy/old
						// conversation with no read-tracking — exclude it to avoid false positives.
						const isUnread = channel.readStatus === false;
						// Awaiting-input conversations have their own count/label and must not
						// also inflate the unread count.
						const isAwaitingInput = awaitingInputIds.has(String(channel.id));
						// Only show indicator for completed agent responses — exclude IN_PROGRESS (streaming),
						// FAILED, CANCELLED, and legacy channels without this field.
						const hasSuccessfulAgentMessage = channel.last_agent_message_status === 'SUCCESSFUL';
						const timestamp = channel.last_message_timestamp;
						const isRecent =
							!!timestamp && now - new Date(timestamp).getTime() < LOOK_BACK_PERIOD_MS;
						return isUnread && !isAwaitingInput && hasSuccessfulAgentMessage && isRecent;
					})
					.sort((a, b) => {
						const aTime = a.last_message_timestamp
							? new Date(a.last_message_timestamp).getTime()
							: 0;
						const bTime = b.last_message_timestamp
							? new Date(b.last_message_timestamp).getTime()
							: 0;
						return bTime - aTime;
					});

				const mostRecentUnread = recentUnread[0]
					? {
							id: String(recentUnread[0].id),
							name: recentUnread[0].name ?? null,
							completedAt: recentUnread[0].last_message_timestamp ?? null,
						}
					: null;

				const result: UnreadConversationSummary = {
					hasUnread: recentUnread.length > 0,
					unreadCount: recentUnread.length,
					inProgressCount,
					awaitingInputCount,
					mostRecentAwaitingInput,
					mostRecentInProgress,
					mostRecentUnread,
					isStatusReady: true,
				};

				// Keep sessionStorage and hook consumers on the same freshly processed summary.
				latestWrittenFetchSequence = fetchSequence;
				writeCache(result, now);
				if (!cancelled) {
					setStatus(result);
				}
			} catch {
				// Fail silently — keep the current displayed status rather than forcing NO_UNREAD.
				// If stale data was being shown, it continues to show — better UX than a false negative.
			} finally {
				isFetchingRef.current = false;
			}
		};

		fetchAndUpdateRef.current = fetchAndUpdate;

		if (!hasMarkedInitialCacheStaleRef.current) {
			// Keep the initial cached render instant, then force the normal
			// post-paint fetch path to revalidate even when the cache is within TTL.
			markUnreadCacheStale();
			hasMarkedInitialCacheStaleRef.current = true;
		}

		// Defer the initial fetch until after paint/idle (post-vc90 safe)
		const cancelPostPaint = schedulePostPaint(() => {
			void fetchAndUpdate();
		});

		// Refresh on window focus when the cache is stale/expired, while preserving the cached value.
		const unbindFocus = isBrowser()
			? bindAll(window, [
					{
						type: 'focus',
						listener: () => {
							void fetchAndUpdate();
						},
					},
				])
			: null;

		return () => {
			cancelled = true;
			cancelPostPaint();
			unbindFocus?.();
		};
	}, [assistanceService, enabled]);

	useEffect(() => {
		if (!enabled) {
			prevModeRef.current = mode;
			return;
		}
		if (mode === undefined && prevModeRef.current !== undefined) {
			// User closed the sidebar — mark cache stale and re-fetch in the background
			markUnreadCacheStale();
			void fetchAndUpdateRef.current();
		}
		prevModeRef.current = mode;
	}, [mode, enabled]);

	const hasInProgress = status.inProgressCount > 0;

	useEffect(() => {
		// Killswitch is read once per polling lifecycle (intentionally not an effect dep): toggling it
		// affects future in-progress polling cycles, not one that is already active. Exposure is
		// suppressed because this is an operational killswitch, not a measured experiment.
		const isPollingDisabled = FeatureGates.getExperimentValue<boolean>(
			'rovo_button_in_progress_polling_killswitch',
			'value',
			false,
			{ fireExperimentExposure: false },
		);
		if (isPollingDisabled || !enabled || !hasInProgress || !isBrowser()) {
			return;
		}

		const refetch = () => {
			// Force a real network fetch, skipping the cache read.
			void fetchAndUpdateRef.current({ forceNetwork: true });
		};

		// Poll while a message is in progress, backing off over time: start at the base
		// interval and add a step every N attempts, until the interval reaches the cache
		// window — at which point polling stops (the next focus/mount/sidebar-close refreshes).
		let attemptsAtCurrentInterval = 0;
		let currentIntervalMs = IN_PROGRESS_POLL_BASE_INTERVAL_MS;
		let timeoutId: ReturnType<typeof setTimeout>;

		const scheduleNext = () => {
			timeoutId = setTimeout(onTick, currentIntervalMs);
		};

		function onTick() {
			// Skip ticks while the tab is backgrounded: do not fetch and do not advance the
			// backoff; simply re-schedule at the same interval.
			if (document.visibilityState !== 'visible') {
				scheduleNext();
				return;
			}

			refetch();
			attemptsAtCurrentInterval += 1;
			if (attemptsAtCurrentInterval >= IN_PROGRESS_POLL_ATTEMPTS_PER_STEP) {
				attemptsAtCurrentInterval = 0;
				currentIntervalMs += IN_PROGRESS_POLL_BACKOFF_STEP_MS;
			}

			// Once the interval reaches the cache window, stop polling entirely.
			if (currentIntervalMs >= IN_PROGRESS_POLL_MAX_INTERVAL_MS) {
				return;
			}

			scheduleNext();
		}

		scheduleNext();

		const unbindVisibility = bindAll(document, [
			{
				type: 'visibilitychange',
				listener: () => {
					// Catch-up refetch when the tab becomes visible again. This does not count
					// toward the backoff and does not alter the pending schedule.
					if (document.visibilityState === 'visible') {
						refetch();
					}
				},
			},
		]);

		return () => {
			clearTimeout(timeoutId);
			unbindVisibility();
		};
	}, [enabled, hasInProgress]);

	return status;
}
